import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ApprovalInboxTable from '../components/approval/ApprovalInboxTable';
import MySubmissionsPanel from '../components/approval/MySubmissionsPanel';
import { useApproval } from '../context/ApprovalContext';
import { usePermissions } from '../hooks/usePermissions';

const tabSx = {
  minHeight: 44,
  '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0', bgcolor: '#0a326d' },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#64748b',
    minHeight: 44,
    px: 2,
    py: 0,
    '&:hover': { color: '#0a326d' },
  },
  '& .Mui-selected': { color: '#0a326d', fontWeight: 700 },
};

export default function Approvals() {
  const location = useLocation();
  const [tab, setTab] = useState(location?.state?.openTab === 'submissions' ? 1 : 0);
  const highlightApprovalId = location?.state?.highlightApprovalId;
  const { inboxCount } = useApproval();
  const { canFeature } = usePermissions();

  const showInbox       = canFeature('/approvals', 'approvals-inbox');
  const showSubmissions = canFeature('/approvals', 'approvals-submissions');
  const showBothTabs    = showInbox && showSubmissions;

  const inboxBadge = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <span>Pending Approval</span>
      {inboxCount > 0 && (
        <Box sx={{ bgcolor: '#dc2626', borderRadius: '20px', px: 0.875, minWidth: 20, textAlign: 'center', py: 0.25 }}>
          <Typography sx={{ color: 'white', fontSize: '0.625rem', fontWeight: 700, lineHeight: 1.5 }}>
            {inboxCount > 99 ? '99+' : inboxCount}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 3.5, display: 'flex', alignItems: 'center', gap: 1.75 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #0a326d 0%, #1a4d9e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(10,50,109,0.22)',
          }}
        >
          <FactCheckOutlinedIcon sx={{ fontSize: 22, color: 'white' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.1 }}>
            Approvals
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.2, display: 'block' }}>
            {showInbox && !showSubmissions
              ? 'Review and action pending content requests'
              : showSubmissions && !showInbox
                ? 'Track the status of your submitted content'
                : 'Review and manage content approval requests'}
          </Typography>
        </Box>
      </Box>

      {/* Tab bar — only when both panels are accessible */}
      {showBothTabs && (
        <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabSx}>
            <Tab label={inboxBadge} />
            <Tab label="My Submissions" />
          </Tabs>
        </Box>
      )}

      {/* Section heading — only when tab bar is hidden (single-panel roles) */}
      {!showBothTabs && (showInbox || showSubmissions) && (
        <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.9375rem' }}>
            {showInbox ? inboxBadge : 'My Submissions'}
          </Typography>
        </Box>
      )}

      {/* Content */}
      {showBothTabs ? (
        <>
          {tab === 0 && <ApprovalInboxTable highlightApprovalId={highlightApprovalId} />}
          {tab === 1 && <MySubmissionsPanel />}
        </>
      ) : showInbox ? (
        <ApprovalInboxTable highlightApprovalId={highlightApprovalId} />
      ) : showSubmissions ? (
        <MySubmissionsPanel />
      ) : null}
    </Box>
  );
}
